import humanizeString from 'humanize-string'
import type { FormEvent } from 'react'
import type { FormProps, InputProps } from 'react-aria-components'
import { useSubmit } from 'react-router'
import type { FormFieldProps } from '#ui/FormField.tsx'

type InputTypes =
  | 'code'
  | 'email'
  | 'inviteCode'
  | 'mode'
  | 'password'
  | 'passwordConfirmation'
  | 'redirectTo'
  | 'remember'
  | 'terms'
  | 'tz'
  | 'username'
  | 'usernameOrEmail'
type InputTypeProps<T extends FormFieldProps> = { [key in InputTypes]: T }

function getRacProps(type: InputTypes): FormFieldProps {
  return {
    ...(getInputProps(type) as FormFieldProps),
    isRequired: true,
    label: humanizeString(type),
    size: 'xl',
    validationBehavior: 'native',
  }
}

function getInputProps(type: string): InputProps {
  return {
    inputMode: type === 'email' ? 'email' : 'text',
    maxLength: 100,
    minLength: 6,
    name: type,
    type: type === 'password' || type === 'email' ? type : 'text',
  }
}

export function useFormFieldProps<T extends object = FormFieldProps>(
  types: InputTypes[],
  errors: FormProps['validationErrors'] | undefined
): [InputTypeProps<T>, FormProps] {
  const submit = useSubmit()
  const onSubmit = (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    submit(evt.currentTarget)
  }

  const inputProps = {} as InputTypeProps<T>
  for (const type of types) {
    const base = getRacProps(type) as T
    if (type === 'email') {
      inputProps.email = { ...base, label: 'Email address' }
      continue
    }
    if (type === 'password') {
      inputProps.password = { ...base, minLength: 8 }
      continue
    }
    if (type === 'passwordConfirmation') {
      inputProps.passwordConfirmation = {
        ...base,
        type: 'password',
        minLength: 8,
      }
      continue
    }
    inputProps[type] = base
  }

  return [inputProps, { onSubmit, method: 'post', validationErrors: errors }]
}

export function useInputProps<T extends string[]>(types: T) {
  const inputProps = {} as { [key: string]: InputProps }
  for (const type of types) {
    inputProps[type] = getInputProps(type)
  }

  return inputProps
}
